import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';

import { GeneralSigninPrompt } from '@/components/global-nav/signin-prompt/general-signin-prompt';
import { wacToken } from '@/libraries/design/foundations';
import { useGlobalNavContext } from '@/patterns/global-nav/src/nav-links-container';

import { messages } from '../messages';
import { fontFamily } from '../util';

export const LoggedOutUser = ({
	onLogInClick,
	isUseGeneralSigninPromptConfig = false,
	setIsUseGeneralSigninPromptConfig,
}: {
	readonly onLogInClick: () => void;
	readonly isUseGeneralSigninPromptConfig?: boolean;
	readonly setIsUseGeneralSigninPromptConfig?: Dispatch<SetStateAction<boolean>>;
}) => {
	const { dropdownRefCurrentNode } = useGlobalNavContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (createAnalyticsEvent && isUseGeneralSigninPromptConfig) {
			createAnalyticsEvent({
				eventName: 'gwp.generalSigninPrompt.modalDisplayed',
				eventType: 'ui',
				action: 'displayed',
				actionSubject: 'modal',
				actionSubjectId: 'GeneralSigninPromptModalDisplayed',
			}).fire();
		}
	}, [createAnalyticsEvent, isUseGeneralSigninPromptConfig]);

	return (
		<>
			<div>
				<Button appearance="subtle" name="sign-in" onClick={onLogInClick} role="link">
					<div css={rootContainer}>
						<div css={triggerContainer}>
							<span css={titleLabel}>
								{isUseGeneralSigninPromptConfig ? (
									<FormattedMessage {...messages.logInAlternative} />
								) : (
									<FormattedMessage {...messages.logIn} />
								)}
							</span>
						</div>
					</div>
				</Button>
			</div>
			{!dropdownRefCurrentNode && (
				<GeneralSigninPrompt
					isUseGeneralSigninPromptConfig={isUseGeneralSigninPromptConfig}
					setIsUseGeneralSigninPromptConfig={setIsUseGeneralSigninPromptConfig}
				/>
			)}
		</>
	);
};

const rootContainer = css({
	'&:hover': {
		cursor: 'pointer',
	},
});

const triggerContainer = css({
	display: 'flex',
	alignItems: 'center',
});

const titleLabel = css({
	fontFamily: fontFamily,
	fontSize: token('space.200'),
	color: wacToken('color.extended.blue800'),
});
